import { useContext, useCallback } from 'react';
import { EditorContext } from '@tiptap/react';
import { observer } from 'mobx-react-lite';
import styled from 'styled-components';
import { Tooltip } from '@mui/material';
import { CloudDoneOutlined, CloudOutlined } from '@mui/icons-material';

// --- UI Primitives ---
import { Spacer } from 'containers/Tiptap/components/tiptap-ui-primitive/spacer';

// --- App UI ---
import { Button } from 'components/Button';
import { ActionButton } from 'containers/Tiptap/components/tiptap-templates/ActionButton';

// --- Store ---
import type EditorStore from 'Tiptap/stores/EditorStore';
import { useStore } from 'utils/hooks/store';
import { DocumentAccessLevel } from 'utils/api/modules/entities/entities.types';

// --- Export utils ---
import { downloadBlob, ExportFormat, safeExport } from 'containers/Tiptap/lib/export-utils';
import { useDocumentVariables } from 'containers/Tiptap/hooks/use-document-variables';
import { customNodes } from 'Tiptap/editor-config/extensions/export-custom-nodes';
import { formatMoment } from 'utils/formatters/timezone';
import { DEFAULT_DATE_FORMAT_WITH_YEAR_AND_HOUR } from 'core/constants';

// --- Styles ---
import 'containers/Tiptap/components/tiptap-templates/notion-like/notion-like-editor-header.scss';

const SavedIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: ${({ theme }) => theme.palette.text.secondary};
  font-size: 13px;
  white-space: nowrap;
`;

const SAVED_TOOLTIP_TEXT =
  'Your changes save automatically as a draft. Click "Publish" to save them as a version you can restore to later.';

interface NotionEditorHeaderProps {
  editorStore: EditorStore;
  onPublish: () => void;
  currentUserUuid?: string | null;
  initialLastSavedAt?: string | null;
  initialLastSavedBy?: { uuid: string; name: string } | null;
}

export const getSaveStatusText = (
  lastSavedBy: { uuid: string; name: string } | null,
  lastSavedAt: string | null,
  hasUnpublishedChanges: boolean,
  currentUserUuid: string | null | undefined
): string => {
  const draft = hasUnpublishedChanges ? ' · Draft' : '';

  if (!lastSavedAt) {
    return hasUnpublishedChanges ? 'Auto-saved · Draft' : 'Auto-saved';
  }

  const byWhom = lastSavedBy?.uuid === currentUserUuid ? 'you' : (lastSavedBy?.name ?? 'unknown');
  const prefix = byWhom === 'you' ? 'Saved by' : 'Last saved by';

  return `${prefix} ${byWhom} at ${formatMoment(lastSavedAt, DEFAULT_DATE_FORMAT_WITH_YEAR_AND_HOUR)}${draft}`;
};

const getPublishButtonText = (isSaving: boolean, hasUnsavedChanges: boolean, hasBeenPublished: boolean): string => {
  if (isSaving) {
    return 'Publishing...';
  }
  if (hasBeenPublished && !hasUnsavedChanges) {
    return 'Published';
  }
  return 'Publish';
};

export const NotionEditorHeader = observer(function NotionEditorHeader({
  editorStore,
  onPublish,
  currentUserUuid,
  initialLastSavedAt,
  initialLastSavedBy,
}: NotionEditorHeaderProps): JSX.Element {
  const { editor } = useContext(EditorContext)!;
  const { documentsDetails, session, notification } = useStore();
  const isViewOnly = documentsDetails.accessLevel === DocumentAccessLevel.View;
  const canShare = !isViewOnly;
  const variables = useDocumentVariables();

  const exportAs = useCallback(
    async (format: ExportFormat) => {
      if (!editor) return;
      if (!(await session.refreshTiptapTokens())) {
        notification.enqueueErrorSnackbar(`Could not export ${format.toUpperCase()}. Please try again.`);
        return;
      }
      safeExport(editor, format, customNodes, session.user?.tiptapConvertToken || '', variables)
        .then((blob) => downloadBlob(blob, `${variables.documentName}.${format}`))
        .catch((error) => console.error(`${format.toUpperCase()} export failed:`, error));
    },
    [editor, session, notification, variables.documentName, variables.documentVersion]
  );

  const handleExportPdf = useCallback(() => exportAs('pdf'), [exportAs]);
  const handleExportDocx = useCallback(() => exportAs('docx'), [exportAs]);

  return (
    <header className="notion-like-editor-header">
      <Spacer />
      <div className="notion-like-editor-header-actions">
        {!isViewOnly && (
          <>
            <Tooltip title={SAVED_TOOLTIP_TEXT} arrow placement="bottom">
              <SavedIndicator data-testid="document-last-saved">
                {editorStore.hasUnpublishedChanges ? (
                  <CloudOutlined sx={{ fontSize: 16 }} />
                ) : (
                  <CloudDoneOutlined sx={{ fontSize: 16 }} />
                )}
                {getSaveStatusText(
                  editorStore.lastSavedBy ?? initialLastSavedBy ?? null,
                  editorStore.lastSavedAt ?? initialLastSavedAt ?? null,
                  editorStore.hasUnpublishedChanges,
                  currentUserUuid
                )}
              </SavedIndicator>
            </Tooltip>
            <Button
              id="save-version-button"
              onClick={onPublish}
              variant={editorStore.hasUnpublishedChanges ? 'contained' : 'outlined'}
              size="medium"
              disabled={editorStore.isPublishing || !editorStore.hasUnpublishedChanges}
            >
              {getPublishButtonText(
                editorStore.isPublishing,
                editorStore.hasUnpublishedChanges,
                editorStore.versions.length > 0
              )}
            </Button>
          </>
        )}
        {canShare && (
          <Button variant="outlined" size="medium" onClick={() => editorStore.setShareModalOpen(true)}>
            Share
          </Button>
        )}
        <ActionButton
          activeSidebar={editorStore.activeSidebar}
          setActiveSidebar={editorStore.setActiveSidebar}
          onExportPdf={handleExportPdf}
          onExportDocx={handleExportDocx}
        />
      </div>
    </header>
  );
});
